import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { PageHeader } from "@/components/page-header";
import { EntranceForm } from "@/components/site-setup/entrances";
import type { SiteEntrance } from "@/components/site-setup-types";
import { FormErrorAlert } from "@/components/stone/form-errors";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { getErrorMessage } from "@/lib/form-errors";
import { trpc } from "@/utils/trpc";

export default function SiteEntranceNew() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { id: siteId } = useParams<{ id: string }>();
  const [submitError, setSubmitError] = useState<string | null>(null);

  const site = useQuery({
    ...trpc.getSite.queryOptions({ id: siteId || "" }),
    enabled: Boolean(siteId),
  });

  const createEntranceMutation = useMutation({
    ...trpc.createSiteEntrance.mutationOptions(),
    onSuccess: () => {
      setSubmitError(null);
      toast.success("Entrance created successfully!");
      queryClient.invalidateQueries({
        queryKey: trpc.getSite.queryOptions({ id: siteId || "" }).queryKey,
      });
      navigate(`/sites/${siteId}`);
    },
    onError: (error) => {
      const message = getErrorMessage(error, "Failed to create entrance");
      setSubmitError(message);
      toast.error(message);
    },
  });

  const handleSaveEntrance = async (entranceData: SiteEntrance) => {
    if (!siteId) return;
    const { plusCode, ...rest } = entranceData;
    await createEntranceMutation.mutateAsync({
      siteId,
      ...rest,
      ...(plusCode ? { plusCode } : {}),
    });
  };

  if (site.isLoading) {
    return <div>Loading...</div>;
  }

  if (site.isError) {
    return (
      <FormErrorAlert
        message={getErrorMessage(site.error, "Failed to load site")}
      />
    );
  }

  if (!site.data) {
    return <FormErrorAlert title="Not found" message="Site not found" />;
  }

  const hasSiteLocation =
    typeof site.data.lat === "number" && typeof site.data.lon === "number";

  return (
    <>
      <PageHeader
        title="Add Entrance"
        description={`Create a new entrance for ${site.data.name}.`}
      />

      {hasSiteLocation ? (
        <EntranceForm
          siteLat={site.data.lat ?? undefined}
          siteLon={site.data.lon ?? undefined}
          onSave={handleSaveEntrance}
          onCancel={() => navigate(`/sites/${siteId}`)}
          submitError={submitError}
          onClearSubmitError={() => setSubmitError(null)}
        />
      ) : (
        <Card>
          <CardHeader>
            <CardTitle>Site location required</CardTitle>
            <CardDescription>
              Set the main site location before adding entrances so the map and
              geofence start in the right place.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button asChild variant="outline">
              <Link to={`/sites/${siteId}/edit`}>Edit site location</Link>
            </Button>
          </CardContent>
        </Card>
      )}
    </>
  );
}
